import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * Footer — shared app-shell bottom bar with Cakewalk brand stripes.
 *
 * Default layout: mint / blue / purple bands, ink panel, centred legal links,
 * and `© <year> Cakewalk Benefits. All rights reserved.`
 *
 * Use at the bottom of cockpit, wallet, enrollment, and marketing flows.
 * Pass `renderLink` (or `linkAsChild` + `linkAs`) for router-aware apps.
 */

export interface FooterLink {
  /** Stable key for the link (defaults to `String(label)`). */
  id?: string;
  label: React.ReactNode;
  href?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

/** Default in-app legal links for cockpit, wallet, and enrollment flows. */
export const DEFAULT_FOOTER_LINKS: FooterLink[] = [
  { id: "privacy", label: "Privacy Policy", href: "/privacy-policy" },
  { id: "terms", label: "Terms of Service", href: "/terms-of-service" },
];

/** @deprecated Prefer `DEFAULT_FOOTER_LINKS`. */
export const APP_SHELL_BRAND_FOOTER_LINKS = DEFAULT_FOOTER_LINKS;

const defaultCopyright = () =>
  `© ${new Date().getFullYear()} Cakewalk Benefits. All rights reserved.`;

export interface FooterProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  /** Legal / utility links. Defaults to Privacy Policy + Terms of Service. */
  links?: readonly FooterLink[];
  /**
   * Copyright line below the links. Defaults to the current-year Cakewalk line.
   * Pass an empty string to hide.
   */
  copyright?: React.ReactNode;
  /**
   * Custom link renderer (e.g. react-router `<Link>`). Takes precedence over
   * `linkAsChild` when both are set.
   */
  renderLink?: (link: FooterLink & { id: string }, children: React.ReactNode) => React.ReactNode;
  /** Render each link via Radix Slot (router-aware wrapper). */
  linkAsChild?: boolean;
  /** Element type for links when `linkAsChild` is true. Default `<a>`. */
  linkAs?: React.ElementType;
  /** Nav accessible name. Default "Legal links". */
  navAriaLabel?: string;
  /** Hide the mint / blue / purple brand stripes. Default false. */
  hideBrandStripes?: boolean;
  /** Replaces the link row and copyright inside the ink panel (stripes still show). */
  children?: React.ReactNode;
}

function resolveLinkId(link: FooterLink): string {
  return link.id ?? String(link.label);
}

export const Footer = React.forwardRef<HTMLElement, FooterProps>(function Footer(
  {
    children,
    className,
    copyright,
    hideBrandStripes = false,
    linkAs = "a",
    linkAsChild = false,
    links = DEFAULT_FOOTER_LINKS,
    navAriaLabel = "Legal links",
    renderLink,
    ...props
  },
  ref,
) {
  const resolvedCopyright = copyright ?? defaultCopyright();

  return (
    <footer ref={ref} className={cx("cw-v2-footer", className)} {...props}>
      {hideBrandStripes ? null : (
        <div className="cw-v2-footer__stripes" aria-hidden="true">
          <div className="cw-v2-footer__stripe cw-v2-footer__stripe--mint" />
          <div className="cw-v2-footer__stripe cw-v2-footer__stripe--blue" />
          <div className="cw-v2-footer__stripe cw-v2-footer__stripe--purple" />
        </div>
      )}
      <div className="cw-v2-footer__legal">
        {children ?? (
          <>
            {links.length > 0 ? (
              <nav aria-label={navAriaLabel} className="cw-v2-footer__nav">
                {links.map((link) => {
                  const id = resolveLinkId(link);
                  const linkWithId = { ...link, id };

                  if (renderLink) {
                    return (
                      <React.Fragment key={id}>{renderLink(linkWithId, link.label)}</React.Fragment>
                    );
                  }

                  return (
                    <FooterLinkItem
                      key={id}
                      asChild={linkAsChild}
                      elementType={linkAs}
                      link={link}
                    />
                  );
                })}
              </nav>
            ) : null}
            {resolvedCopyright === "" ? null : (
              <p className="cw-v2-footer__copyright">{resolvedCopyright}</p>
            )}
          </>
        )}
      </div>
    </footer>
  );
});
Footer.displayName = "Footer";

function FooterLinkItem({
  asChild,
  elementType,
  link,
}: {
  asChild: boolean;
  elementType: React.ElementType;
  link: FooterLink;
}) {
  const Comp = asChild ? Slot : elementType;
  return (
    <Comp className="cw-v2-footer__link" href={link.href} onClick={link.onClick}>
      {link.label}
    </Comp>
  );
}
